import { enumLabel } from '../../../../utils/enumLabels';
import { Card, Descriptions, Form, Input, Radio, Typography, Space, Tag, Divider } from 'antd';
import {
  UserOutlined,
  IdcardOutlined,
  CalendarOutlined,
  ManOutlined,
  WomanOutlined,
  MedicineBoxOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';
import { clearWizardDraft, readWizardDraft, writeWizardDraft } from '../../../../utils/wizardDraft';
import {
  MultiCheckWithOtherField,
  OCCUPATIONAL_EXPOSURE_OPTIONS,
  OTHER_OPTION_VALUE,
} from './anamnesisFields';
import ScreeningParticipation from './ScreeningParticipation';

const { Title, Paragraph, Text } = Typography;

interface Step1Draft {
  occupation: string;
  occupationalExposure?: string;
  occupationalExposureTypes: string[];
  exposureDetails: string;
}

const Step1Identification = () => {
  const { patient, form, loading, saveForm } = useDermatoWizard();
  const [occupation, setOccupation] = useState<string>(
    (form?.generalData?.['occupation'] as string) ?? ''
  );
  const [occupationalExposure, setOccupationalExposure] = useState<string | undefined>(
    (form?.generalData?.['occupationalExposure'] as string) ?? undefined
  );
  const [occupationalExposureTypes, setOccupationalExposureTypes] = useState<string[]>(
    Array.isArray(form?.generalData?.['occupationalExposureTypes'])
      ? ((form.generalData['occupationalExposureTypes'] as unknown[]).filter((x): x is string => typeof x === 'string'))
      : []
  );
  const [exposureDetails, setExposureDetails] = useState<string>(
    (form?.generalData?.['occupationalExposureDetails'] as string) ?? ''
  );
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const skipNextFormSync = useRef(false);

  useEffect(() => {
    if (skipNextFormSync.current) {
      skipNextFormSync.current = false;
      return;
    }
    const draft = form?.id ? readWizardDraft<Step1Draft>(form.id, 'identification') : null;
    if (draft) {
      setOccupation(draft.occupation ?? '');
      setOccupationalExposure(draft.occupationalExposure);
      setOccupationalExposureTypes(Array.isArray(draft.occupationalExposureTypes) ? draft.occupationalExposureTypes : []);
      setExposureDetails(draft.exposureDetails ?? '');
      setDirty(true);
      return;
    }
    setOccupation((form?.generalData?.['occupation'] as string) ?? '');
    setOccupationalExposure((form?.generalData?.['occupationalExposure'] as string) ?? undefined);
    const storedTypes = form?.generalData?.['occupationalExposureTypes'];
    const types = Array.isArray(storedTypes)
      ? storedTypes.filter((x): x is string => typeof x === 'string')
      : [];
    const storedDetails = (form?.generalData?.['occupationalExposureDetails'] as string) ?? '';
    // Compat: text liber vechi → „Altul” + text.
    if (!types.length && storedDetails.trim()) {
      setOccupationalExposureTypes([OTHER_OPTION_VALUE]);
    } else {
      setOccupationalExposureTypes(types);
    }
    setExposureDetails(storedDetails);
    setDirty(false);
  }, [form]);

  useEffect(() => {
    if (!dirty || !form?.id) return;
    const timer = window.setTimeout(() => {
      writeWizardDraft(form.id, 'identification', {
        occupation,
        occupationalExposure,
        occupationalExposureTypes,
        exposureDetails,
      } satisfies Step1Draft);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [occupation, occupationalExposure, occupationalExposureTypes, exposureDetails, dirty, form?.id]);

  const persist = async (): Promise<boolean> => {
    if (!form) return true;
    if (!dirty) return true;
    try {
      setSaving(true);
      const saved = await saveForm({
        generalData: {
          ...(form.generalData ?? {}),
          occupation,
          occupationalExposure,
          occupationalExposureTypes: occupationalExposure === 'yes' ? occupationalExposureTypes : [],
          occupationalExposureDetails: occupationalExposure === 'yes' ? exposureDetails : '',
        },
      });
      if (!saved) return false;
      clearWizardDraft(form.id, 'identification');
      skipNextFormSync.current = true;
      setDirty(false);
      return true;
    } finally {
      setSaving(false);
    }
  };

  // „Salvează și continuă" / navigarea din sidebar folosesc același handler.
  useRegisterStepSave(persist);

  if (loading || !patient) {
    return <Card loading title="Identificare pacient" />;
  }

  return (
    <>
    <Card
      title={
        <Space>
          <UserOutlined style={{ color: '#1890ff' }} />
          <span>Informații Pacient</span>
        </Space>
      }
    >
      <Descriptions column={2} bordered size="small">
        <Descriptions.Item label={<><IdcardOutlined /> CNP</>}>
          {patient.cnp}
        </Descriptions.Item>
        <Descriptions.Item label={<><UserOutlined /> Nume</>}>
          {patient.lastName} {patient.firstName}
        </Descriptions.Item>
        <Descriptions.Item label={<><CalendarOutlined /> Data nașterii</>}>
          {new Date(patient.dateOfBirth).toLocaleDateString('ro-RO')}
        </Descriptions.Item>
        <Descriptions.Item label="Sex">
          {patient.sex === 'M' ? (
            <><ManOutlined style={{ color: '#1890ff' }} /> Masculin</>
          ) : (
            <><WomanOutlined style={{ color: '#eb2f96' }} /> Feminin</>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={<><PhoneOutlined /> Telefon</>}>
          {patient.phone ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label={<><MailOutlined /> E-mail</>}>
          {patient.email ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label={<><HomeOutlined /> Locație</>} span={2}>
          {patient.city ?? '—'}, {patient.county ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label={<><MedicineBoxOutlined /> Program</>} span={2}>
          <Tag color="blue">{enumLabel(patient.programType)}</Tag>
          {patient.status && <Tag color={patient.status === 'ACTIVE' ? 'green' : 'default'}>{enumLabel(patient.status)}</Tag>}
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      <Title level={5} style={{ marginTop: 0 }}>Detalii înrolare (opțional)</Title>
      <Paragraph type="secondary">
        Aceste date se salvează automat în <code>form.generalData</code> la apăsarea „Salvează detalii"
        sau „Salvează și continuă".
      </Paragraph>

      <Form layout="vertical">
        <Form.Item label="Ocupație">
          <Input
            value={occupation}
            onChange={(e) => {
              setOccupation(e.target.value);
              setDirty(true);
            }}
            placeholder="ex: inginer, profesor, agricultor…"
          />
        </Form.Item>
        <Form.Item label="Expunere profesională la substanțe periculoase" required>
          <Radio.Group
            value={occupationalExposure}
            onChange={(e) => {
              const next = e.target.value as string;
              setOccupationalExposure(next);
              if (next !== 'yes') {
                setOccupationalExposureTypes([]);
                setExposureDetails('');
              }
              setDirty(true);
            }}
          >
            <Radio value="no">Nu</Radio>
            <Radio value="yes">Da</Radio>
            <Radio value="unknown">Necunoscut</Radio>
          </Radio.Group>
        </Form.Item>
        {occupationalExposure === 'yes' && (
          <MultiCheckWithOtherField
            label="Dacă da, specificați tipul de expunere"
            value={occupationalExposureTypes}
            options={OCCUPATIONAL_EXPOSURE_OPTIONS}
            otherOptionValue={OTHER_OPTION_VALUE}
            otherLabel="Dacă altul, specificați care"
            otherValue={exposureDetails}
            otherPlaceholder="ex: azbest, benzen, zgomot…"
            onChange={(v) => {
              setOccupationalExposureTypes(v);
              setDirty(true);
            }}
            onOtherChange={(v) => {
              setExposureDetails(v);
              setDirty(true);
            }}
          />
        )}
        <Form.Item>
          <button
            type="button"
            onClick={() => void persist()}
            disabled={saving || !dirty}
            style={{
              padding: '6px 16px',
              background: saving || !dirty ? '#d9d9d9' : '#1890ff',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: saving || !dirty ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Se salvează…' : 'Salvează detalii'}
          </button>
          {dirty && (
            <Text type="secondary" style={{ marginLeft: 12 }}>
              Modificări nesalvate
            </Text>
          )}
        </Form.Item>
      </Form>

      <div style={{ marginTop: 16, padding: 12, background: '#f0f2f5', borderRadius: 4 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Pasul 1 conține identificarea pacientului (preluată din registrul de pacienți).
          Câmpurile ocupație + expunere profesională sunt opționale dar ajută la scorul de risc.
        </Text>
      </div>
    </Card>

    {/* 2.9.3 — PAS 3: acceptul/refuzul participării se consemnează la înrolare,
        înainte de chestionarul de anamneză și risc (vezi documentul medical,
        secțiunea „1. Traseul pacientului – pe scurt"). */}
    <div style={{ marginTop: 16 }}>
      <ScreeningParticipation />
    </div>
    </>
  );
};

export default Step1Identification;
